import { faInfoCircle } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cx from 'classnames';
import React, { useEffect, useRef } from 'react';
import { FormGroup, Label, Input } from 'reactstrap';
import type { InputType } from 'reactstrap/types/lib/Input';

import { blue } from '../../app/colors';
import { useThemeContext } from '../../app/theme-provider';
import SingleErrorDisplay from '../error-display/single-error-display';
import { useFormFieldContext } from '../form-hook';
import { Tooltip } from '../tooltip';

import styles from './input-field.module.scss';

interface InputFieldProps {
  label?: string;
  id?: string;
  required?: boolean;
  name: string;
  formGroup?: { className?: string };
  type?: InputType;
  maxLength?: number;
  spellCheck?: boolean;
  autoResize?: boolean;
  placeholder?: string;
  information?: string;
  disabled?: boolean;
  onChange?: (event: any) => undefined;
}

export default function InputField({
  label,
  name,
  id,
  required,
  type,
  spellCheck,
  autoResize,
  information,
  disabled = false,
  ...rest
}: InputFieldProps) {
  const { value, isNotValid, register } = useFormFieldContext(name);
  const { theme } = useThemeContext();
  const registration = register(name);
  const ref = useRef(null);
  const cloneRef = (refValue: any) => {
    registration.innerRef(refValue);
    ref.current = refValue;
  };
  const handleResize = () => {
    const element = ref.current as any;
    if (element && autoResize && type === 'textarea') {
      element.style.height = 'auto';
      element.style.height = `${element.scrollHeight + 20}px`;
    }
  };
  // TODO CCRA-37242
  useEffect(handleResize, [value]);

  const fieldType = type || 'text';
  return (
    <FormGroup>
      {label ? (
        <>
          <Label className={cx('me-2', { required, stay: value })} for={name}>
            {label}
          </Label>
          {information && (
            <Tooltip tooltip={information}>
              <FontAwesomeIcon
                icon={faInfoCircle}
                size="lg"
                color={theme === 'dark' ? '#52baff' : blue}
                tabIndex={0}
              />
            </Tooltip>
          )}
        </>
      ) : null}
      <Input
        id={id || name}
        {...registration}
        {...rest}
        innerRef={cloneRef}
        defaultValue={value || ''}
        disabled={disabled}
        invalid={isNotValid}
        type={fieldType}
        name={name}
        className={`${styles.input} form-control mb-2`}
        autoComplete="off"
        aria-required={required}
        spellCheck={spellCheck}
      />
      <SingleErrorDisplay field={name} />
    </FormGroup>
  );
}
